import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { isFinite, isNumber } from 'lodash';
import { useMount, usePrevious } from 'react-use';
import { useRouter } from 'next/router';
import { Box, Button, Stack, Typography } from '@mui/material';
import { getWindow, slugify, trackEvent } from '@surfline/web-common';
import { CalendarIcon } from '@wavetrak/theme';

import { PRODUCT_TUTORIALS } from 'common/constants';
import { SL_SCIENCE_POC_CONDITION_SIMILARITY } from 'common/treatments';
import ForecastGraphConditionRatings from 'components/ForecastGraphConditionRatings';
import ForecastGraphWind from 'components/ForecastGraphWind';
import ForecastSummary from 'components/ForecastSummary';
import ForecastGraphSurf from 'components/ForecastGraphSurf';
import ForecastGraphsWeatherContainer from 'components/ForecastGraphsWeatherContainer';
import ForecastGraphTide from 'components/ForecastGraphTide';
import Information from 'components/Information';
import ForecastGraphAdvancedSwell from 'components/ForecastGraphAdvancedSwell';
import ForecastGraphAccordion from 'components/ForecastGraphAccordion';
import ForecastGraphEnergy from 'components/ForecastGraphEnergy';
import ForecastGraphConsistency from 'components/ForecastGraphConsistency';
import ForecastGraphSwellSpectra from 'components/ForecastGraphSwellSpectra';
import SwellSignaturePoc from 'components/SwellSignaturePoc';
import ForecastGraphsSunlight from 'components/ForecastGraphsSunlight';
import WavetrakLink from 'components/WavetrakLink';
import FeaturePaywall from 'components/FeaturePaywall/FeaturePaywall';
import useAccordionToggle from 'hooks/useAccordionToggle';
import { useMaxWidthTablet, useMaxWidthTabletLarge } from 'hooks/useMediaQueries';
import { useAppSelector } from 'stores/hooks';
import { getSpotDetails } from 'selectors/spot';
import { getSubregionOverviewData } from 'selectors/subregion';
import { useUserPermissionStatus } from 'selectors/user';
import type { ConditionData } from 'types/conditionRating';
import type { LiveWindGraphData } from 'types/liveWind';
import type { ConsistencyData } from 'types/consistency';
import type { SubregionDaySummaryData } from 'types/subregion';
import type { SunlightTimes } from 'types/sunlightTimes';
import type { SwellSpectraData } from 'types/spectra';
import type { SurfData } from 'types/surf';
import type { SwellsData } from 'types/swell';
import type { TideData } from 'types/tide';
import type { Weather } from 'types/weather';
import type { WaveEnergyGraphData } from 'types/waveEnergy';
import type { WindData } from 'types/wind';
import { getEnergyKjFromTooltipDatum } from 'utils/getEnergyKjFromTooltipDatum';
import midnightOfDate from 'utils/midnightOfDate';
import { useTreatments } from 'utils/treatments';

import SurfTooltip from '../GraphTooltips/SurfTooltip';
import ConsistencyTooltip from '../GraphTooltips/ConsistencyTooltip';
import EnergyTooltip from '../GraphTooltips/EnergyTooltip';
import GraphTooltipProvider, {
  type GraphId,
} from '../GraphTooltips/GraphTooltipProvider/GraphTooltipProvider';
import SwellTooltip from '../GraphTooltips/SwellTooltip';
import TideHeightTooltip from '../GraphTooltips/TideHeightTooltip';
import WindTooltip from '../GraphTooltips/WindTooltip';

import useTimestamps from '../hooks/useTimestamps';
import useTooltipData from '../hooks/useTooltipData';
import useStoredSwellSpectraGraphToggle from '../hooks/useStoredSwellSpectraGraphToggle';
import useSwipeHandlers from '../hooks/useSwipeHandlers';

import styles from './GraphContainer.module.scss';

interface Props {
  consistency: ConsistencyData;
  consistencyEnabled: boolean;
  daySummary: SubregionDaySummaryData;
  daysAllowed: number;
  daysToDisplay: Array<number>;
  firstDayIndex: number;
  graphContainerRef: MutableRefObject<HTMLDivElement | null>;
  hasLiveWind: boolean;
  isHistoricView: boolean;
  lastDayIndex: number;
  liveWind: LiveWindGraphData;
  ratings: ConditionData;
  setSwipeLeftCount: Dispatch<SetStateAction<number>>;
  setSwipeRightCount: Dispatch<SetStateAction<number>>;
  spectraDataAvailable: boolean;
  spotId: string;
  spotName: string;
  sunlightTimes: SunlightTimes;
  supportsInsights: boolean;
  surf: SurfData;
  swellSpectra: SwellSpectraData;
  swells: SwellsData;
  tide: TideData;
  utcOffset: number;
  waveEnergy: WaveEnergyGraphData;
  weather: Weather;
  wind: WindData;
}

const SURF_GRAPH_SEGMENT_PROPS = {
  location: 'surf_height_graph',
  pageName: 'Spot Report',
};

const WIND_GRAPH_SEGMENT_PROPS = {
  location: 'wind_graph',
  pageName: 'Spot Report',
};

const GraphContainer: React.FC<Props> = ({
  consistency,
  consistencyEnabled,
  daySummary,
  daysToDisplay,
  firstDayIndex,
  graphContainerRef,
  hasLiveWind,
  isHistoricView,
  lastDayIndex,
  liveWind,
  ratings,
  setSwipeLeftCount,
  setSwipeRightCount,
  spectraDataAvailable,
  spotId,
  spotName,
  sunlightTimes,
  surf,
  swellSpectra,
  swells,
  tide,
  utcOffset,
  waveEnergy,
  weather,
  wind,
}) => {
  const { query } = useRouter();
  const tideChartsPath = `/tide-charts/${slugify(spotName)}/${spotId}`;
  const isConsistencyVisible = !isHistoricView && consistencyEnabled;

  const isGraphFullBleed = useMaxWidthTabletLarge();
  const isMobile = useMaxWidthTablet();
  const spot = useAppSelector(getSpotDetails);
  const subregionOverview = useAppSelector(getSubregionOverviewData);
  const [displayEnergy, setDisplayEnergy] = useState<boolean>(false);
  const { hasSwellSpectraPermissions, hasCoreForecastPermissions, hasLiveWindPermissions } =
    useUserPermissionStatus();
  const surfGraphTopRef = useRef<HTMLDivElement | null>(null);
  const [hoveredSwellIndex, setHoveredSwellIndex] = useState(-1);
  const previousHoveredSwellIndex = usePrevious(hoveredSwellIndex);
  const [isMounted, setMounted] = useState(false);
  const treatments = useTreatments();

  // Use loading state for D3 graphs during SSR to prevent hydration mismatch
  const isGraphLoading = !isMounted;

  const isPocSimilarityEnabled =
    treatments[SL_SCIENCE_POC_CONDITION_SIMILARITY] === 'on' && !isMobile;

  const { isAccordionOpen: isSwellGraphOpen, toggleAccordion: toggleSwellGraph } =
    useAccordionToggle({
      id: 'advancedSwell',
    });
  const { isAccordionOpen: isSwellSigOpen, toggleAccordion: toggleSwellSigGraph } =
    useAccordionToggle({
      id: 'swellSig',
    });
  const { isAccordionOpen: isEnergyGraphOpen, toggleAccordion: toggleEnergyGraph } =
    useAccordionToggle({
      id: 'energy',
    });
  const { isAccordionOpen: isConsistencyGraphOpen, toggleAccordion: toggleConsistencyGraph } =
    useAccordionToggle({
      id: 'consistency',
    });
  const { graphOpen: isSwellSpectraOpen, toggleGraph: toggleSwellSpectraOpen } =
    useStoredSwellSpectraGraphToggle({
      id: 'swellSpectra',
    });

  const isNative = query?.native?.toString() === 'true';
  const upgradeUrl = isNative ? 'surfline://upgrade' : '/upgrade';

  const midnightOfStartDate = useMemo(
    () =>
      isHistoricView && swells?.days?.[0]?.[0]?.timestamp && swells?.days?.[0]?.[0]?.utcOffset
        ? new Date(midnightOfDate(swells.days[0][0].utcOffset, swells.days[0][0].timestamp) * 1000)
        : new Date(midnightOfDate(utcOffset) * 1000),
    [isHistoricView, utcOffset, swells?.days],
  );

  const { timestamps, initialXPercentage, xScale } = useTimestamps({
    firstDayIndex,
    graphContainerRef,
    isMounted,
    midnightOfStartDate,
    lastDayIndex,
  });

  const overallMaxPeriod = useMemo(
    () => (isFinite(swellSpectra?.overallMaxPeriod) ? swellSpectra.overallMaxPeriod : 0),
    [swellSpectra.overallMaxPeriod],
  );

  const graphWidth = useMemo(() => {
    const range = xScale?.range();
    return range?.length === 2 ? range[1] - range[0] : 0;
  }, [xScale]);

  const graphHeight = useCallback(
    () => graphContainerRef?.current?.clientHeight ?? 0,
    [graphContainerRef],
  );
  const graphTop = useCallback(
    () =>
      (graphContainerRef?.current?.getBoundingClientRect()?.top ?? 0) +
      (getWindow()?.pageYOffset ?? 0) -
      120,
    [graphContainerRef],
  ); // offset for header height

  const trackGraphScrubbed = useCallback(
    (graphId: GraphId, additionalSegmentProps: { [key: string]: string | number }) => {
      const hasObservations =
        graphId === 'surf-height' &&
        firstDayIndex === 0 &&
        surf.observations?.some((o) => o.surf !== null);
      trackEvent('Graph Scrubbed', {
        forecastStart: firstDayIndex + 1,
        forecastEnd: lastDayIndex + 1,
        graphType: graphId,
        moduleName: 'forecast graph',
        pageName: 'Spot Report',
        spotId,
        spotName,
        ...(hasObservations && { hasObservedWaveHeight: true }),
        ...additionalSegmentProps,
      });
    },
    // Important: the flag used to skip tracking is reset when these props change
    [firstDayIndex, lastDayIndex, spotId, spotName, surf.observations],
  );

  const tooltipData = useTooltipData({
    consistency,
    daysToDisplay,
    initialXPercentage,
    isGraphFullBleed,
    liveWind: liveWind?.data,
    ratings,
    surf,
    swellSpectra,
    swells,
    tide,
    waveEnergy,
    wind,
    xScale,
  });

  const nextButtonDisabled = useMemo(
    () =>
      lastDayIndex === 15 ||
      surf.loading ||
      swells.loading ||
      waveEnergy.loading ||
      !!surf.error ||
      !!swells.error ||
      !!waveEnergy.error,
    [
      lastDayIndex,
      surf.error,
      surf.loading,
      swells.error,
      swells.loading,
      waveEnergy.error,
      waveEnergy.loading,
    ],
  );
  const prevButtonDisabled = useMemo(
    () =>
      firstDayIndex === 0 ||
      surf.loading ||
      swells.loading ||
      waveEnergy.loading ||
      !!surf.error ||
      !!swells.error ||
      !!waveEnergy.error,
    [
      firstDayIndex,
      surf.error,
      surf.loading,
      swells.error,
      swells.loading,
      waveEnergy.error,
      waveEnergy.loading,
    ],
  );

  const toggleAdvancedSwellGraph = useCallback(() => {
    toggleSwellGraph();
    setHoveredSwellIndex(-1);
  }, [toggleSwellGraph]);

  const isWindLoading = useMemo(
    () =>
      (hasLiveWind ? liveWind?.loading : !!wind?.loading) ||
      !!ratings?.loading ||
      !!sunlightTimes?.loading ||
      (hasLiveWind && liveWind?.loading),
    [hasLiveWind, liveWind?.loading, ratings?.loading, sunlightTimes?.loading, wind?.loading],
  );

  const onSwipeLeft = useCallback(() => {
    setSwipeLeftCount((prev: number) => prev + 1);
  }, [setSwipeLeftCount]);

  const onSwipeRight = useCallback(() => {
    setSwipeRightCount((prev: number) => prev + 1);
  }, [setSwipeRightCount]);

  const swipeHandlers = useSwipeHandlers({
    leftSwipeEnabled: !nextButtonDisabled,
    rightSwipeEnabled: !prevButtonDisabled,
    onSwipeLeft,
    onSwipeRight,
  });

  const activeSurfItem = useMemo(
    () => ({
      day: daysToDisplay[tooltipData?.surf?.dayIndex],
      hour: tooltipData?.surf?.hourIndex,
      condition: tooltipData?.rating?.data,
      timestamp: tooltipData?.surf?.timestamp,
      utcOffset: tooltipData?.surf?.utcOffset,
    }),
    [
      daysToDisplay,
      tooltipData?.rating?.data,
      tooltipData?.surf?.dayIndex,
      tooltipData?.surf?.hourIndex,
      tooltipData?.surf?.timestamp,
      tooltipData?.surf?.utcOffset,
    ],
  );

  const activeWindItem = useMemo(
    () => ({
      day: daysToDisplay[tooltipData?.wind?.dayIndex],
      hour: tooltipData?.wind?.hourIndex,
      condition: tooltipData?.rating?.data,
      timestamp: tooltipData?.wind?.timestamp,
    }),
    [
      daysToDisplay,
      tooltipData?.rating?.data,
      tooltipData?.wind?.dayIndex,
      tooltipData?.wind?.hourIndex,
      tooltipData?.wind?.timestamp,
    ],
  );

  const graphSegmentProps = useMemo(
    () => ({
      forecastPeriod: daysToDisplay[0],
      spotId,
      spotName,
      subregionId: subregionOverview?._id,
      subregionName: subregionOverview?.name,
      tabName: 'report',
    }),
    [daysToDisplay, spotId, spotName, subregionOverview?._id, subregionOverview?.name],
  );

  const surfGraphSegmentProps = useMemo(
    () => ({
      ...graphSegmentProps,
      modalType: 'tutorial',
      uiElement: 'surf-height',
    }),
    [graphSegmentProps],
  );

  const windGraphSegmentProps = useMemo(
    () => ({
      ...graphSegmentProps,
      modalType: 'tutorial',
      uiElement: 'wind',
    }),
    [graphSegmentProps],
  );

  const tideGraphSegmentProps = useMemo(
    () => ({
      ...graphSegmentProps,
      modalType: 'tutorial',
      uiElement: 'tide',
    }),
    [graphSegmentProps],
  );

  const advancedSwellGraphInfo = useMemo(
    () => ({
      articleId: PRODUCT_TUTORIALS.articles.swellHeight.id,
      segmentProps: {
        ...graphSegmentProps,
        modalType: 'tutorial',
        uiElement: 'swell',
      },
      tooltip: PRODUCT_TUTORIALS.articles.swellHeight.tooltip,
      tutorialId: PRODUCT_TUTORIALS.tutorials.default.id,
    }),
    [graphSegmentProps],
  );

  const waveEnergyGraphInfo = useMemo(
    () => ({
      articleId: PRODUCT_TUTORIALS.articles.waveEnergy.id,
      segmentProps: {
        ...graphSegmentProps,
        modalType: 'tutorial',
        uiElement: 'energy',
      },
      tooltip: PRODUCT_TUTORIALS.articles.waveEnergy.tooltip,
      tutorialId: PRODUCT_TUTORIALS.tutorials.default.id,
    }),
    [graphSegmentProps],
  );

  const consistencyGraphInfo = useMemo(
    () => ({
      articleId: PRODUCT_TUTORIALS.articles.waveConsistency.id,
      segmentProps: {
        ...graphSegmentProps,
        modalType: 'tutorial',
        uiElement: 'consistency',
      },
      tooltip: PRODUCT_TUTORIALS.articles.waveConsistency.tooltip,
      tutorialId: PRODUCT_TUTORIALS.tutorials.default.id,
    }),
    [graphSegmentProps],
  );

  const swellSpectraGraphInfo = useMemo(
    () => ({
      articleId: PRODUCT_TUTORIALS.articles.swellSpectra.id,
      segmentProps: {
        ...graphSegmentProps,
        modalType: 'tutorial',
        uiElement: 'spectra',
      },
      tooltip: 'Swell Spectra',
      tutorialId: PRODUCT_TUTORIALS.tutorials.default.id,
    }),
    [graphSegmentProps],
  );

  const graphAccordionLabelSwell = useMemo(() => {
    if (displayEnergy) return 'Swell (kJ)';
    if (swells?.units?.waveHeight) return `Swell (${swells.units.waveHeight?.toLowerCase()})`;
    return 'Swell';
  }, [displayEnergy, swells?.units?.waveHeight]);

  const graphAccordionLabelSpectra = useMemo(
    () => (
      <span>
        Swell Spectra (m<sup>2</sup>s)
      </span>
    ),
    [],
  );

  const hasAllSpectraData = useMemo(() => {
    const spectraDataPoint = swellSpectra?.days?.[0]?.[0];

    return (
      (!!spectraDataPoint?.direction &&
        !!spectraDataPoint?.energy &&
        !!spectraDataPoint?.spread &&
        swellSpectra?.period) ||
      swellSpectra?.loading ||
      swellSpectra?.error
    );
  }, [swellSpectra?.days, swellSpectra?.error, swellSpectra?.loading, swellSpectra?.period]);

  const location = useMemo(() => ({ lat: spot.lat, lon: spot.lon }), [spot.lat, spot.lon]);

  const userUnits = useMemo(
    () => ({
      ...wind.units,
      ...swells.units,
      ...tide.units,
    }),
    [wind.units, swells.units, tide.units],
  );

  const surfTooltipUnits = useMemo(
    () => ({ ...swells.units, ...surf.units }),
    [surf.units, swells.units],
  );

  useEffect(() => {
    if (
      hoveredSwellIndex !== previousHoveredSwellIndex &&
      isNumber(previousHoveredSwellIndex) &&
      previousHoveredSwellIndex >= 0
    ) {
      trackEvent('Swell Selected', {
        forecastStart: firstDayIndex + 1,
        forecastEnd: lastDayIndex + 1,
        spotId,
        spotName,
        subregionId: subregionOverview?._id,
        subregionName: subregionOverview?.name,
        swellNumber: hoveredSwellIndex + 1,
        tabName: 'report',
      });
    }
    // We only want this to run when activeSwell changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoveredSwellIndex, previousHoveredSwellIndex]);

  useMount(() => {
    setMounted(true);
  });

  const showSurfGraphPaywall = !hasCoreForecastPermissions;
  const showWindPaywall = !hasLiveWindPermissions || !hasCoreForecastPermissions;
  const windPaywallText = spot?.hasLiveWind
    ? 'Get wind data every 10 minutes'
    : 'View hourly wind data';

  return (
    <Stack direction="column" position="relative" {...swipeHandlers}>
      <Box position="relative">
        <ForecastSummary
          days={daySummary.days}
          daysToDisplay={daysToDisplay}
          error={daySummary.error}
          graphHeight={graphHeight()}
          graphTop={graphTop()}
          graphWidth={graphWidth}
          historicDates={swells.days}
          isHistoricView={isHistoricView}
          lastPublished={daySummary.lastPublished}
          loading={daySummary.loading}
          mode="graph"
          spotId={spotId}
          spotName={spotName}
          timezone={daySummary.timezone}
        />
        {isPocSimilarityEnabled && (
          <Box mb={8}>
            <ForecastGraphAccordion
              id="swellSig"
              isOpen={isSwellSigOpen}
              label="Similar Condition Images"
              onChange={toggleSwellSigGraph}
              // @ts-ignore
              labs
            >
              <Box className={styles.swellGraphContainer}>
                <SwellSignaturePoc
                  spotId={spotId}
                  utcOffset={utcOffset}
                  activeSurf={tooltipData.surf}
                  graphWidth={graphWidth}
                  isMobile={isMobile}
                  tide={tide}
                  wind={wind}
                  userUnits={userUnits}
                />
              </Box>
            </ForecastGraphAccordion>
          </Box>
        )}
        <Box className="sl-section-full-bleed" position="relative">
          <ForecastGraphConditionRatings
            activeRating={tooltipData.rating}
            daysToDisplay={daysToDisplay}
            graphWidth={graphWidth}
            leftNowPosition={tooltipData?.surf?.leftNowPosition}
            leftPosition={tooltipData?.surf?.leftPosition}
            loading={ratings?.loading}
            ratings={ratings}
            renderTooltip={tooltipData.renderTooltip}
            sunlightTimes={sunlightTimes}
            timeLabel={tooltipData?.surf?.timeLabel}
            trackGraphScrubbed={trackGraphScrubbed}
          />
        </Box>
        <Box className={styles.surfGraphTooltipContainer}>
          <SurfTooltip
            activeSurf={tooltipData.surf}
            activeSwells={tooltipData.swells}
            conditionsRating={
              tooltipData?.rating?.data?.rating ? tooltipData.rating.data.rating : 'NONE'
            }
            graphWidth={graphWidth}
            isMobile={isGraphFullBleed}
            units={surfTooltipUnits}
            observationData={surf.observations || []}
          />
        </Box>
        <Box
          className={classNames({
            [styles.surfGraphSection]: true,
            'sl-section-full-bleed': true,
            [styles.surfGraphSectionPaywall]: showSurfGraphPaywall,
          })}
        >
          <div className={styles.information}>
            <Information
              articleId={PRODUCT_TUTORIALS.articles.surfHeight.id}
              segmentProps={surfGraphSegmentProps}
              tooltip={PRODUCT_TUTORIALS.articles.surfHeight.tooltip}
              tutorialId={PRODUCT_TUTORIALS.tutorials.default.id}
            />
          </div>

          <GraphTooltipProvider
            graphId="surf-height"
            customStyle={classNames({
              [styles.surfGraphVerticalBarRating]: ratings?.days && !ratings?.loading,
              [styles.surfGraphVerticalBarNoRating]: !ratings?.days && !ratings?.loading,
            })}
            graphWidth={graphWidth}
            leftNowPosition={tooltipData?.surf?.leftNowPosition}
            leftPosition={tooltipData?.surf?.leftPosition}
            renderTooltip={tooltipData.renderTooltip}
            timeLabel={tooltipData?.surf?.timeLabel}
            trackGraphScrubbed={trackGraphScrubbed}
          >
            <ForecastGraphSurf
              activeItem={activeSurfItem}
              data={surf}
              dataTestId="surf-graph"
              daysToDisplay={daysToDisplay}
              isMobile={isMobile}
              loading={isGraphLoading || surf.loading}
              showTimeline
              sunlightTimes={sunlightTimes}
            />
          </GraphTooltipProvider>
        </Box>
        {showSurfGraphPaywall && (
          <Box className={styles.featurePaywallWrapper}>
            <Box className="sl-section-full-bleed">
              <FeaturePaywall
                text="View hourly forecast data"
                segmentProperties={SURF_GRAPH_SEGMENT_PROPS}
                upgradeUrl={upgradeUrl}
              />
            </Box>
          </Box>
        )}
        <Box className={styles.windTooltipContainer}>
          <WindTooltip
            activeWind={tooltipData.wind}
            graphWidth={graphWidth}
            isMobile={isGraphFullBleed}
            units={wind.units || liveWind.units}
            location={location}
          />
        </Box>
        <Box
          className={classNames({
            [styles.windGraphSection]: true,
            'sl-section-full-bleed': true,
            [styles.windGraphSectionPaywall]: showWindPaywall,
          })}
        >
          <div className={styles.information}>
            <Information
              articleId={PRODUCT_TUTORIALS.articles.wind.id}
              segmentProps={windGraphSegmentProps}
              tooltip={PRODUCT_TUTORIALS.articles.wind.tooltip}
              tutorialId={PRODUCT_TUTORIALS.tutorials.default.id}
            />
          </div>
          <GraphTooltipProvider
            customStyle={classNames({
              [styles.windGraphVerticalBar]: true,
              [styles.windGraphVerticalBarObserved]: true,
            })}
            graphId="wind"
            graphWidth={graphWidth}
            leftNowPosition={tooltipData?.wind?.leftNowPosition}
            leftPosition={tooltipData?.wind?.leftPosition}
            renderTooltip={tooltipData.renderTooltip}
            timeLabel={tooltipData?.wind?.timeLabel}
            trackGraphScrubbed={trackGraphScrubbed}
          >
            <ForecastGraphWind
              activeItem={activeWindItem}
              liveWind={liveWind?.data || null}
              wind={wind}
              loading={isGraphLoading || isWindLoading}
              daysToDisplay={daysToDisplay}
              sunlightTimes={sunlightTimes}
              isMobile={isMobile}
              activeWind={tooltipData.wind}
            />
          </GraphTooltipProvider>
        </Box>
        {showWindPaywall && (
          <Box className={styles.featurePaywallWrapper}>
            <Box className="sl-section-full-bleed">
              <FeaturePaywall
                text={windPaywallText}
                segmentProperties={WIND_GRAPH_SEGMENT_PROPS}
                upgradeUrl={upgradeUrl}
              />
            </Box>
          </Box>
        )}
        {(tide?.days?.length || tide.loading || tide.error) && (
          <>
            <Box className={styles.tideGraphHeader}>
              <Typography variant="callout1" className={styles.tideGraphHeading} component="h3">
                Tides{' '}
                {tide.units?.tideHeight && (
                  <Typography
                    variant="callout1"
                    component="span"
                    className={styles.tideGraphHeadingUnits}
                  >
                    ({tide.units?.tideHeight})
                  </Typography>
                )}
              </Typography>
              {tide.tideLocation?.name && (
                <Typography variant="caption1" className={styles.tideGraphSubheading} component="p">
                  {tide.tideLocation.name}
                </Typography>
              )}
              <div className={styles.information}>
                <Information
                  articleId={PRODUCT_TUTORIALS.articles.tide.id}
                  segmentProps={tideGraphSegmentProps}
                  tooltip={PRODUCT_TUTORIALS.articles.tide.tooltip}
                  tutorialId={PRODUCT_TUTORIALS.tutorials.default.id}
                />
              </div>
              {!tide.error && (
                <Button
                  className={styles.tideGraphButton}
                  component={WavetrakLink}
                  data-testid="graph-tide-chart-button"
                  href={tideChartsPath}
                  size="small"
                  startIcon={<CalendarIcon />}
                  variant="secondary"
                >
                  Tide calendar
                </Button>
              )}
            </Box>
            <Box className={classNames(styles.tideGraphSection, 'sl-section-full-bleed')}>
              <TideHeightTooltip
                tideHeight={tooltipData?.tide?.data?.current?.height}
                units={tide.units?.tideHeight}
                leftPosition={tooltipData?.tide?.leftPosition}
                graphWidth={graphWidth}
              />
              <GraphTooltipProvider
                customStyle={styles.tideGraphVerticalBar}
                customStyleNow={styles.tideGraphVerticalBarNow}
                graphId="tide"
                graphWidth={graphWidth}
                leftNowPosition={tooltipData?.tide?.leftNowPosition}
                leftPosition={tooltipData?.tide?.leftPosition}
                renderTooltip={tooltipData.renderTooltip}
                timeLabel={tooltipData?.tide?.timeLabel}
                trackGraphScrubbed={trackGraphScrubbed}
              >
                <ForecastGraphTide
                  daysToDisplay={daysToDisplay}
                  endTimestamp={timestamps.end}
                  tide={tide}
                  startTimestamp={timestamps.start}
                  sunlightTimes={sunlightTimes}
                />
              </GraphTooltipProvider>
            </Box>
          </>
        )}
        <Box className={styles.sunlightGraphSection}>
          <ForecastGraphsSunlight daysToDisplay={daysToDisplay} sunlightTimes={sunlightTimes} />
        </Box>
        <div
          className={classNames('sl-section-full-bleed', styles.graphDivider)}
          ref={surfGraphTopRef}
        />
        <ForecastGraphAccordion
          id="swell"
          isOpen={isSwellGraphOpen}
          label={graphAccordionLabelSwell}
          onChange={toggleAdvancedSwellGraph}
          information={advancedSwellGraphInfo}
        >
          {isMobile && (
            <Box className={styles.swellGraphMobileTooltip}>
              <SwellTooltip
                activeWave={tooltipData.swells}
                activeSwell={hoveredSwellIndex}
                setActiveSwell={setHoveredSwellIndex}
                graphWidth={graphWidth / daysToDisplay.length}
                units={swells.units}
              />
            </Box>
          )}
          <Box className={styles.swellGraphContainer}>
            <GraphTooltipProvider
              customStyle={classNames(
                styles.swellGraphVerticalBar,
                styles.swellGraphVerticalBarEnergy,
              )}
              graphId="swell"
              graphWidth={graphWidth}
              leftNowPosition={tooltipData?.swells?.leftNowPosition}
              leftPosition={tooltipData?.swells?.leftPosition}
              renderTooltip={tooltipData.renderTooltip}
              timeLabel={tooltipData?.swells?.timeLabel}
              trackGraphScrubbed={trackGraphScrubbed}
            >
              <ForecastGraphAdvancedSwell
                activeItem={activeSurfItem}
                activeSwell={hoveredSwellIndex}
                daysToDisplay={daysToDisplay}
                displayEnergy={setDisplayEnergy}
                graphWidth={graphWidth}
                hoveredSwell={hoveredSwellIndex}
                isMobile={isMobile}
                onHoveredSwell={setHoveredSwellIndex}
                onSelectSwell={setHoveredSwellIndex}
                segmentProps={graphSegmentProps}
                sunlightTimes={sunlightTimes}
                swells={swells}
                tooltipLeftPosition={tooltipData?.swells?.leftPosition}
              />
            </GraphTooltipProvider>
          </Box>
        </ForecastGraphAccordion>
      </Box>
      <ForecastGraphAccordion
        id="energy"
        information={waveEnergyGraphInfo}
        isOpen={isEnergyGraphOpen}
        label="Nearshore Energy (kJ)"
        onChange={toggleEnergyGraph}
      >
        <EnergyTooltip
          graphWidth={graphWidth}
          tooltipLeftPosition={
            tooltipData?.energy?.leftPosition ?? tooltipData?.swells?.leftPosition
          }
          energy={getEnergyKjFromTooltipDatum(tooltipData?.energy?.data)}
        />
        <GraphTooltipProvider
          customStyle={classNames(styles.swellGraphVerticalBar, styles.waveEnergyGraphVerticalBar)}
          customStyleNow={classNames(
            styles.swellGraphVerticalBar,
            styles.waveEnergyGraphVerticalBarNow,
          )}
          graphId="energy"
          graphWidth={graphWidth}
          leftNowPosition={tooltipData?.energy?.leftNowPosition}
          leftPosition={tooltipData?.energy?.leftPosition}
          renderTooltip={tooltipData.renderTooltip}
          timeLabel={tooltipData?.energy?.timeLabel}
          trackGraphScrubbed={trackGraphScrubbed}
        >
          <ForecastGraphEnergy
            daysToDisplay={daysToDisplay}
            sunlightTimes={sunlightTimes}
            swells={swells}
            waveEnergy={waveEnergy}
          />
        </GraphTooltipProvider>
      </ForecastGraphAccordion>
      {isConsistencyVisible && (
        <ForecastGraphAccordion
          id="consistency"
          information={consistencyGraphInfo}
          isOpen={isConsistencyGraphOpen}
          label="Wave Consistency"
          onChange={toggleConsistencyGraph}
        >
          <ConsistencyTooltip
            consistency={tooltipData?.consistency?.data?.consistency?.waveCount}
            graphWidth={graphWidth}
            tooltipLeftPosition={tooltipData?.consistency?.leftPosition}
          />
          <GraphTooltipProvider
            customStyle={classNames(
              styles.swellGraphVerticalBar,
              styles.waveConsistencyGraphVerticalBar,
            )}
            customStyleNow={classNames(
              styles.swellGraphVerticalBar,
              styles.waveConsistencyGraphVerticalBarNow,
            )}
            graphId="consistency"
            graphWidth={graphWidth}
            leftNowPosition={tooltipData?.consistency?.leftNowPosition}
            leftPosition={tooltipData?.consistency?.leftPosition}
            renderTooltip={tooltipData.renderTooltip}
            timeLabel={tooltipData?.consistency?.timeLabel}
            trackGraphScrubbed={trackGraphScrubbed}
          >
            <ForecastGraphConsistency
              daysToDisplay={daysToDisplay}
              consistency={consistency}
              sunlightTimes={sunlightTimes}
            />
          </GraphTooltipProvider>
        </ForecastGraphAccordion>
      )}
      {hasSwellSpectraPermissions && spectraDataAvailable && (
        <ForecastGraphAccordion
          id="spectra"
          information={swellSpectraGraphInfo}
          isOpen={isSwellSpectraOpen}
          // The accordion ts does not accept anything other than string values
          label={graphAccordionLabelSpectra as unknown as string}
          onChange={toggleSwellSpectraOpen}
        >
          {hasAllSpectraData && (
            <ForecastGraphSwellSpectra
              activeWave={tooltipData.swells}
              daysToDisplay={daysToDisplay}
              graphWidth={graphWidth}
              overallMaxPeriod={overallMaxPeriod || 0}
              renderTooltip={tooltipData?.renderTooltip}
              segmentProps={graphSegmentProps}
              trackGraphScrubbed={trackGraphScrubbed}
              swellSpectraData={swellSpectra}
              units={swells.units}
            />
          )}
        </ForecastGraphAccordion>
      )}
      {!isHistoricView && (
        <Box className={styles.weatherGraphSection}>
          <ForecastGraphsWeatherContainer
            daysToDisplay={daysToDisplay}
            isMobile={isMobile}
            loading={weather?.loading || sunlightTimes?.loading}
            sunlightTimes={sunlightTimes}
            weather={weather}
          />
        </Box>
      )}
    </Stack>
  );
};

export default GraphContainer;
